import { memo, PropsWithChildren } from 'react';
import { Typography } from '../../../../../components/typography/typography';
import { ProductImageV2 } from '../../../../../components/image/product/product-image-v2';
import { CustomProductLineItem, ProductLineItem } from '../../../../../clients/quipli-service/financials/financials';
import SkeletonLoader from '../../../../../components/skeleton-loader/skeleton-loader';
import { ProductItemState } from '../../hooks/use-product-item-data';

interface ProductItemInfoProps extends ProductItemState, PropsWithChildren {}

export const ProductItemName = ({
  name,
  isNewProduct,
  financialItem,
}: {
  name: string;
  isNewProduct: boolean;
  financialItem?: ProductLineItem | CustomProductLineItem;
}) => {
  if (isNewProduct && !financialItem) {
    return <SkeletonLoader style={{ height: '20px', width: '100px' }} />;
  }

  return (
    <Typography type="text-sm" weight="regular" className="m-[2px] text-quipli-black-800">
      {name}
    </Typography>
  );
};

export const ProductItemOptions = ({
  options,
  productId,
}: {
  options: { id: number; name: string; value: string }[];
  productId: number;
}) => {
  if (options.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      {options.map((option) => (
        <Typography
          key={`product-${productId}-option-${option.id}`}
          type="text-xs"
          weight="medium"
          className="text-quipli-black-800"
        >
          {option.name}: {option.value}
        </Typography>
      ))}
    </div>
  );
};

export const ProductItemInfo = memo<ProductItemInfoProps>(
  ({ productId, isCustomProduct, children }) => {
    const placeHolderOrProductId = isCustomProduct ? 'placeholder' : productId;
    return (
      <>
        <ProductImageV2
          productId={placeHolderOrProductId}
          profile="MINI"
          className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden"
        />
        <div className="ml-[8px] flex flex-col">{children}</div>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for React.memo
    return (
      prevProps.productId === nextProps.productId &&
      prevProps.isCustomProduct === nextProps.isCustomProduct &&
      prevProps.children === nextProps.children
    );
  },
);

ProductItemInfo.displayName = 'ProductItemInfo';
